/* AUTO-SYNCED from PsyKit.io/components/brand/Wordmark.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
import type { CSSProperties } from "react";

/**
 * Psy180° brand wordmark.
 *
 * "Psy" — Cormorant Garamond (font-heading), white.
 * "180" — DM Sans (--font-sans), gold, lining figures (uniform height so it
 *          reads unmistakably as one-eighty, not "Psyi80").
 * "°"   — a CSS ring ("XC": d 0.27em / stroke 0.06em) riding the upper-right of
 *          the final 0; 180° = the turn a client makes in therapy. Sized in `em`
 *          so it scales with whatever font-size the caller sets. A visually-
 *          hidden "°" keeps the accessible name + SEO text as "Psy180°".
 *
 * Caller sets the size via `className` (e.g. "text-[22px]"), matching how the
 * wordmark was sized inline before this component existed.
 */

// "180" — sans figures shrunk + baseline-nudged to sit with the serif caps.
const numStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  color: "var(--color-gold-soft)",
  fontSize: "0.9em",
  position: "relative",
  top: "0.012em",
  marginLeft: "0.02em",
  letterSpacing: "0.005em",
  fontFeatureSettings: '"tnum" 1',
};

// Degree ring "XC" — tweak these three numbers to re-tune size/weight/position.
const degStyle: CSSProperties = {
  display: "inline-block",
  width: "0.27em",
  height: "0.27em",
  border: "0.06em solid var(--color-gold-soft)",
  borderRadius: "50%",
  boxSizing: "border-box",
  position: "relative",
  top: "-0.42em",
  left: "0.02em",
};

export function Wordmark({
  className = "",
  degree = true,
}: {
  className?: string;
  /** Show the ° ring (+ its screen-reader text). Turn off for inline mentions
   *  in headings/prose, where a degree mark next to other words reads oddly. */
  degree?: boolean;
}) {
  return (
    <span
      className={`font-heading font-semibold tracking-wide text-[var(--color-text-primary)] ${className}`.trim()}
    >
      Psy
      <span style={numStyle}>
        180
        {degree ? <span aria-hidden="true" style={degStyle} /> : null}
        {degree ? <span className="sr-only">°</span> : null}
      </span>
    </span>
  );
}
